<template>
  <div class="flex flex-col gap-4 p-4 font-sans text-slate-800 dark:text-slate-200">
    <!-- CABEÇALHO DA ABA PROCESSOS -->
    <div class="flex items-center justify-between pb-3 border-b border-n-slate-4 dark:border-slate-800">
      <div class="flex items-center gap-2">
        <span class="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700/50">
          <span class="i-lucide-scale size-4.5" />
        </span>
        <div>
          <h4 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
            <span>Processos Judiciais</span>
            <span class="px-2 py-0.2 rounded-full text-[11px] font-extrabold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              {{ processesList.length }}
            </span>
          </h4>
          <p class="text-[11px] text-slate-500 dark:text-slate-400">
            Acompanhamento processual vinculado ao cliente
          </p>
        </div>
      </div>

      <button
        type="button"
        @click="openAddModal"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-[#2781F6] hover:bg-[#1b6fd8] rounded-xl shadow-xs transition-all cursor-pointer"
      >
        <span class="i-lucide-plus size-3.5" />
        <span>Novo Processo</span>
      </button>
    </div>

    <!-- LISTA DE PROCESSOS -->
    <div v-if="processesList.length" class="space-y-3">
      <div
        v-for="(proc, index) in processesList"
        :key="index"
        class="p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800/80 shadow-xs hover:border-blue-300 dark:hover:border-blue-700/60 transition-all space-y-2.5"
      >
        <!-- Topo do Card: Número e Status -->
        <div class="flex items-start justify-between gap-2">
          <div class="flex flex-col">
            <span class="text-xs font-black font-mono tracking-tight text-blue-600 dark:text-blue-400 select-all">
              {{ proc.number || 'Número do processo não informado' }}
            </span>
            <span v-if="proc.type" class="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              📋 {{ proc.type }}
            </span>
          </div>

          <div class="flex items-center gap-1.5">
            <span
              v-if="proc.status"
              :class="getStatusBadgeClass(proc.status)"
              class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider"
            >
              {{ proc.status }}
            </span>
            
            <button
              type="button"
              @click="editProcess(index)"
              class="p-1 rounded-md text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Editar Processo"
            >
              <span class="i-lucide-pencil size-3.5" />
            </button>
            <button
              type="button"
              @click="deleteProcess(index)"
              class="p-1 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Remover Processo"
            >
              <span class="i-lucide-trash-2 size-3.5" />
            </button>
          </div>
        </div>

        <!-- Detalhes do Processo -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] pt-2 border-t border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-400">
          <div v-if="proc.parties">
            <span class="font-bold text-slate-700 dark:text-slate-300">Partes:</span> {{ proc.parties }}
          </div>
          <div v-if="proc.court">
            <span class="font-bold text-slate-700 dark:text-slate-300">Vara:</span> {{ proc.court }}
          </div>
          <div v-if="proc.lawyers" class="sm:col-span-2 text-indigo-600 dark:text-indigo-400 font-medium">
            <span class="font-bold text-slate-700 dark:text-slate-300">👨‍⚖️ Advogado(s):</span> {{ proc.lawyers }}
          </div>
          <div v-if="proc.date">
            <span class="font-bold text-slate-700 dark:text-slate-300">Distribuição:</span> {{ formatDate(proc.date) }}
          </div>
          <div v-if="proc.value">
            <span class="font-bold text-slate-700 dark:text-slate-300">Valor Causa:</span> {{ proc.value }}
          </div>
        </div>

        <!-- Link Tribunal -->
        <div v-if="proc.url" class="pt-1 flex items-center justify-end">
          <a
            :href="proc.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            <span>Ver no Tribunal</span>
            <span class="i-lucide-external-link size-3" />
          </a>
        </div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <div
      v-else
      class="py-10 px-4 text-center rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 flex flex-col items-center justify-center gap-2.5"
    >
      <div class="size-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center">
        <span class="i-lucide-scale size-5" />
      </div>
      <div>
        <p class="text-xs font-bold text-slate-800 dark:text-slate-200">Nenhum processo judicial cadastrado</p>
        <p class="text-[11px] text-slate-400">Adicione os números dos processos e ações para acompanhamento direto.</p>
      </div>
      <button
        type="button"
        @click="openAddModal"
        class="mt-1 flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-[#2781F6] hover:bg-[#1b6fd8] rounded-xl shadow-xs transition-all cursor-pointer"
      >
        <span class="i-lucide-plus size-3.5" />
        <span>+ Adicionar Primeiro Processo</span>
      </button>
    </div>

    <!-- MODAL DE CADASTRO / EDIÇÃO DE PROCESSO -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
    >
      <div class="w-full max-w-lg bg-white dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl p-5 space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
          <div class="flex items-center gap-2">
            <span class="p-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <span class="i-lucide-scale size-4" />
            </span>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white">
              {{ editingIndex !== null ? 'Editar Processo Judicial' : 'Adicionar Processo Judicial' }}
            </h3>
          </div>
          <button
            @click="showModal = false"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <form @submit.prevent="saveProcessForm" class="space-y-3 text-xs">
          <div>
            <label class="block font-bold text-slate-700 dark:text-slate-300 mb-1">Número do Processo (CNJ) *</label>
            <input
              v-model="form.number"
              required
              placeholder="Ex: 1003538-65.2026.8.26.0032"
              class="w-full h-8 px-3 font-mono rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Partes Envolvidas</label>
              <input
                v-model="form.parties"
                placeholder="Ex: Autor x Réu / Fazenda"
                class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Vara / Comarca</label>
              <input
                v-model="form.court"
                placeholder="Ex: 1ª Vara Cível - Foro Central"
                class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block font-semibold text-slate-700 dark:text-slate-300">👨‍⚖️ Advogado(s) Vinculado(s)</label>
              <div v-if="isDeMarcoAccount" class="flex items-center gap-1">
                <button
                  type="button"
                  @click="addLawyerChipToForm('Vinicius de Marco Fiscarelli')"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                >
                  + Dr. Vinícius
                </button>
                <button
                  type="button"
                  @click="addLawyerChipToForm('Bruno Alves Roque')"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                >
                  + Dr. Bruno
                </button>
                <button
                  type="button"
                  @click="addLawyerChipToForm('Vanderson Maciel')"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                >
                  + Dr. Vanderson
                </button>
              </div>
            </div>
            <input
              v-model="form.lawyers"
              :placeholder="isDeMarcoAccount ? 'Ex: Vinicius de Marco Fiscarelli / Bruno Alves Roque' : 'Ex: Nome do Advogado / Responsável'"
              class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Tipo da Ação</label>
              <input
                v-model="form.type"
                placeholder="Ex: Cumprimento de Sentença"
                class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Situação / Fase</label>
              <select
                v-model="form.status"
                class="w-full h-8 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6] cursor-pointer"
              >
                <option value="Em Andamento">Em Andamento</option>
                <option value="Aguardando Sentença">Aguardando Sentença</option>
                <option value="Fase Recursal">Fase Recursal</option>
                <option value="Execução / Cumprimento">Execução / Cumprimento</option>
                <option value="Suspenso">Suspenso</option>
                <option value="Encerrado / Arquivado">Encerrado / Arquivado</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Data de Distribuição</label>
              <input
                v-model="form.date"
                type="date"
                class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
            <div>
              <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Valor da Causa / Condenação</label>
              <input
                v-model="form.value"
                placeholder="Ex: R$ 45.000,00"
                class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <div>
            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Link Direto do Processo (Tribunal)</label>
            <input
              v-model="form.url"
              type="url"
              placeholder="Ex: https://esaj.tjsp.jus.br/cpopg/show.do?processo.codigo=..."
              class="w-full h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#2781F6]"
            />
          </div>

          <div class="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              @click="showModal = false"
              class="px-3.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="isSaving"
              class="px-4 py-1.5 font-bold rounded-lg bg-[#2781F6] text-white hover:bg-[#1b6fd8] shadow-xs cursor-pointer disabled:opacity-50"
            >
              {{ isSaving ? 'Salvando...' : 'Salvar Processo' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const showModal = ref(false);
const editingIndex = ref(null);
const isSaving = ref(false);
const currentAccountId = computed(() => Number(store.getters.getCurrentAccountId || 0));
const isDeMarcoAccount = computed(() => currentAccountId.value === 3);

const form = ref({
  number: '',
  parties: '',
  court: '',
  type: '',
  status: 'Em Andamento',
  date: '',
  value: '',
  url: '',
});

const processesList = computed(() => {
  return props.selectedContact?.additional_attributes?.processes || [];
});

const getStatusBadgeClass = (status) => {
  if (status === 'Encerrado / Arquivado') return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
  if (status === 'Aguardando Sentença' || status === 'Execução / Cumprimento') return 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-700/40';
  return 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-700/40';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
};

const openAddModal = () => {
  editingIndex.value = null;
  form.value = {
    number: '',
    parties: '',
    court: '',
    type: '',
    status: 'Em Andamento',
    date: '',
    value: '',
    url: '',
  };
  showModal.value = true;
};

const editProcess = (index) => {
  editingIndex.value = index;
  const current = processesList.value[index] || {};
  form.value = { ...current };
  showModal.value = true;
};

const deleteProcess = async (index) => {
  if (!confirm('Deseja realmente remover este processo judicial do cliente?')) return;
  const updatedProcesses = [...processesList.value];
  updatedProcesses.splice(index, 1);
  await persistProcesses(updatedProcesses, 'Processo judicial removido!');
};

const addLawyerChipToForm = (lawyerName) => {
  if (!form.value.lawyers || !form.value.lawyers.trim()) {
    form.value.lawyers = lawyerName;
  } else if (!form.value.lawyers.includes(lawyerName)) {
    form.value.lawyers = `${form.value.lawyers} / ${lawyerName}`;
  }
};

const saveProcessForm = async () => {
  if (!form.value.number.trim()) return;
  const updatedProcesses = [...processesList.value];

  if (editingIndex.value !== null) {
    updatedProcesses[editingIndex.value] = { ...form.value };
  } else {
    updatedProcesses.push({ ...form.value });
  }

  await persistProcesses(updatedProcesses, 'Processo judicial salvo com sucesso!');
  showModal.value = false;
};

const persistProcesses = async (processes, successMsg) => {
  isSaving.value = true;
  try {
    const additionalAttributes = {
      ...(props.selectedContact.additional_attributes || {}),
      processes: processes,
      process_number: processes[0]?.number || '',
    };

    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      additional_attributes: additionalAttributes,
    });

    useAlert(successMsg);
  } catch (error) {
    console.error('Error saving judicial processes:', error);
    useAlert('Erro ao salvar processo: ' + error.message);
  } finally {
    isSaving.value = false;
  }
};
</script>
